import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Star, CheckCircle, X } from 'lucide-react';
import Layout from '../../components/Layout';
import useAuthStore from '../../store/authStore';
import api from '../../utils/api';
import OnboardingWizard from '../../components/OnboardingWizard';
import { FluxoCandidato, faseAtualCandidato } from '../../components/FluxoJornada';

function DestaquePromo() {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (sessionStorage.getItem('destaque_promo_visto')) return;
    api.get('/candidatos/destaque/status').then(r => {
      if (!r.data?.ativo) setTimeout(() => setShow(true), 1200);
    }).catch(() => {});
  }, []);
  const fechar = () => { sessionStorage.setItem('destaque_promo_visto', '1'); setShow(false); };
  const ir = () => { sessionStorage.setItem('destaque_promo_visto', '1'); navigate('/candidato/destaque'); };
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={fechar}>
      <div className="bg-white rounded-2xl max-w-sm w-full p-6 text-center relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={fechar} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"><X size={20}/></button>
        <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
          <Star size={32} className="text-amber-500" fill="#f59e0b"/>
        </div>
        <h2 className="font-sora font-black text-acre-dark text-xl mb-2">Novidade: Destaque seu perfil!</h2>
        <p className="text-gray-600 text-sm mb-4">Por apenas <strong>R$ 9,80</strong> seu perfil aparece <strong>em primeiro lugar</strong> para as empresas nas vagas compativeis com voce (70%+). Mais visibilidade = mais chances de ser contratado!</p>
        <ul className="text-left text-sm text-gray-600 space-y-1.5 mb-5">
          <li className="flex gap-2"><CheckCircle size={16} className="text-acre-green flex-shrink-0"/> Apareca no topo das buscas das empresas</li>
          <li className="flex gap-2"><CheckCircle size={16} className="text-acre-green flex-shrink-0"/> Vale por 30 dias</li>
          <li className="flex gap-2"><CheckCircle size={16} className="text-acre-green flex-shrink-0"/> Pagamento via Pix, cartao ou boleto</li>
        </ul>
        <button onClick={ir} className="w-full py-3.5 bg-[#009EE3] hover:bg-[#007ab5] text-white font-sora font-black rounded-xl flex items-center justify-center gap-2 transition-colors">
          <Star size={18}/> Quero me destacar
        </button>
        <button onClick={fechar} className="w-full mt-2 text-sm text-gray-400 hover:text-gray-600 py-1">Agora nao</button>
      </div>
    </div>
  );
}

export default function DashboardCandidato() {
  const { user } = useAuthStore();
  const [perfil,         setPerfil]         = useState(null);
  const [matches,        setMatches]        = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [notificacoes,   setNotificacoes]   = useState([]);
  const [curtidas,       setCurtidas]       = useState({});
  const [selecoes,       setSelecoes]       = useState([]);

  const carregarPerfil = () => {
    api.get('/candidatos/perfil').then(r => {
      setPerfil(r.data);
      if (!r.data.onboarding_concluido) {
        setTimeout(() => setShowOnboarding(true), 800);
      }
    }).catch(() => {});
    api.get('/candidatos/matches').then(r => setMatches(r.data.matches || [])).catch(() => {});
    api.get('/candidatos/notificacoes').then(r => setNotificacoes(r.data || [])).catch(() => {});
    api.get('/candidatos/vagas/curtidas').then(r => setCurtidas(r.data || {})).catch(() => {});
    api.get('/candidatos/selecao').then(r => setSelecoes(r.data || [])).catch(() => {});
  };

  const marcarLida = async (id) => {
    await api.post(`/candidatos/notificacoes/${id}/lida`).catch(() => {});
    setNotificacoes(p => p.map(n => n.id === id ? {...n, lida: true} : n));
  };

  const marcarTodasLidas = async () => {
    await api.post('/candidatos/notificacoes/marcar-todas-lidas').catch(() => {});
    setNotificacoes(p => p.map(n => ({...n, lida: true})));
  };

  useEffect(() => { carregarPerfil(); }, []);

  const pct = perfil?.porcentagem_perfil || 0;

  return (
    <Layout title={`Olá, ${user?.nome?.split(' ')[0]}! 👋`} subtitle="Veja seu painel de candidato">

      <DestaquePromo />

      {/* ── Wizard de Onboarding IA ── */}
      {showOnboarding && (
        <OnboardingWizard
          nome={user?.nome?.split(' ')[0] || 'Candidato'}
          modoAtualizacao={!!perfil?.onboarding_concluido}
          onConcluir={() => {
            setShowOnboarding(false);
            carregarPerfil();
          }}
        />
      )}

      {/* Perfil card */}
      <div className="card p-5 mb-5">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-acre-green to-acre-teal flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
            {user?.nome?.charAt(0)}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-sora font-semibold text-acre-dark">{user?.nome}</span>
              {user?.selo_verificado && <span className="badge-gold">⭐ Candidato Ouro</span>}
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              {perfil?.cidade || '—'} · {perfil?.areas_interesse?.join(', ') || 'Sem áreas'}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <Link to="/candidato/perfil" className="btn-outline text-xs px-4 py-2">Editar Perfil</Link>
            <button onClick={() => setShowOnboarding(true)}
              className="text-xs text-acre-green font-semibold flex items-center gap-1 hover:underline">
              {perfil?.onboarding_concluido ? '🔄 Atualizar com IA' : '✨ Criar perfil com IA'}
            </button>
          </div>
        </div>

        {/* Barra de progresso */}
        <div>
          <div className="flex justify-between text-sm mb-1.5">
            <span className="text-gray-600 font-medium">Perfil preenchido</span>
            <span className={`font-bold ${pct === 100 ? 'text-acre-green' : 'text-gray-500'}`}>{pct}%</span>
          </div>
          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-acre-green to-acre-teal transition-all duration-1000"
              style={{ width: `${pct}%` }}
            />
          </div>
          {pct < 100 && (
            <p className="text-xs text-gray-400 mt-1.5">
              Complete seu perfil para ganhar o Selo Candidato Ouro ⭐
            </p>
          )}
          {pct === 100 && (
            <p className="text-xs text-acre-green font-semibold mt-1.5">
              ✅ Perfil 100% completo! Você tem o Selo Candidato Ouro.
            </p>
          )}
        </div>
      </div>

      {/* ── Notificações de seleção e vagas ── */}
      {notificacoes.filter(n => !n.lida).length > 0 && (
        <div className="mb-5 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-acre-dark flex items-center gap-2">
              🔔 Notificações
              <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                {notificacoes.filter(n => !n.lida).length}
              </span>
            </p>
            <button onClick={marcarTodasLidas} className="text-xs text-gray-400 hover:text-acre-green">
              Marcar todas como lidas
            </button>
          </div>
          {notificacoes.filter(n => !n.lida).slice(0, 5).map(n => (
            <div key={n.id} onClick={() => marcarLida(n.id)}
              className={`card p-3 cursor-pointer border-l-4 transition-all hover:shadow-sm ${
                n.tipo === 'em_selecao'
                  ? 'border-l-blue-500 bg-blue-50/40'
                  : n.tipo === 'vaga_atualizada'
                  ? 'border-l-yellow-400 bg-yellow-50/40'
                  : 'border-l-acre-green bg-acre-light/30'
              }`}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-acre-dark">{n.titulo}</p>
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {new Date(n.created_at).toLocaleDateString('pt-BR')}
                </span>
              </div>
              {n.corpo && (
                <p className="text-xs text-gray-600 mt-1 leading-relaxed whitespace-pre-line">
                  {n.corpo.replace(/\*\*/g, '')}
                </p>
              )}
              {n.tipo === 'em_selecao' && (
                <Link to="/candidato/perfil" onClick={e => e.stopPropagation()}
                  className="mt-2 text-xs text-blue-600 font-semibold hover:underline inline-flex items-center gap-1">
                  → Verifique seu perfil e aguarde o contato
                </Link>
              )}
              <p className="text-xs text-gray-400 mt-1">Toque para marcar como lida</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Fluxo da jornada ── */}
      <FluxoCandidato faseAtual={faseAtualCandidato({
        pct,
        onboarding:      !!perfil?.onboarding_concluido,
        totalCurtidas:   Object.values(curtidas).filter(a => a === 'curtiu').length,
        foiSelecionado:  selecoes.length > 0,
        foiAprovado:     selecoes.some(s => s.status_selecao === 'aprovado'),
      })} />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <div className="card p-4 text-center">
          <div className="font-sora text-2xl font-bold text-acre-dark">{matches.length}</div>
          <div className="text-xs text-gray-400 mt-0.5">Vagas compatíveis</div>
        </div>
        <div className="card p-4 text-center">
          <div className="font-sora text-2xl font-bold text-acre-gold">
            {matches[0]?.score_total ? `${matches[0].score_total}%` : '—'}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">Melhor match</div>
        </div>
        <div className="card p-4 text-center">
          <div className="font-sora text-2xl font-bold text-blue-500">{pct}%</div>
          <div className="text-xs text-gray-400 mt-0.5">Perfil completo</div>
        </div>
      </div>

      {/* Dicas de melhoria */}
      {pct < 100 && (
        <div className="card p-4 mb-5 border-l-4 border-acre-gold">
          <h3 className="font-sora font-semibold text-sm text-acre-dark mb-3">📋 Para completar seu perfil:</h3>
          <div className="space-y-2">
            {!perfil?.resumo_profissional  && <div className="flex items-center gap-2 text-sm text-gray-600"><span className="w-4 h-4 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-xs">○</span>Adicionar resumo profissional</div>}
            {!perfil?.video_pitch_url      && <div className="flex items-center gap-2 text-sm text-gray-600"><span className="w-4 h-4 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-xs">○</span>Adicionar link do vídeo pitch (60s)</div>}
            {!perfil?.link_portfolio       && <div className="flex items-center gap-2 text-sm text-gray-600"><span className="w-4 h-4 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-xs">○</span>Adicionar link do portfólio</div>}
            {!perfil?.areas_interesse?.length && <div className="flex items-center gap-2 text-sm text-gray-600"><span className="w-4 h-4 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-xs">○</span>Selecionar áreas de interesse</div>}
          </div>
          <Link to="/candidato/perfil" className="btn-primary inline-block mt-3 text-xs px-4 py-2">Completar agora</Link>
        </div>
      )}

      {/* Melhores matches */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="section-title">Suas Melhores Vagas</h2>
          <Link to="/candidato/matches" className="text-xs text-acre-green font-semibold hover:underline">Ver todas →</Link>
        </div>
        {matches.length === 0 ? (
          <div className="card p-8 text-center">
            <p className="text-4xl mb-2">🎯</p>
            <p className="text-gray-500 text-sm">Complete seu perfil para ver vagas compatíveis.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {matches.slice(0, 4).map(m => (
              <div key={m.vaga_id} className="card p-4 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex flex-col items-center justify-center text-white flex-shrink-0 text-xs font-bold
                  ${m.score_total >= 80 ? 'bg-green-500' : m.score_total >= 60 ? 'bg-lime-500' : 'bg-yellow-500'}`}>
                  <span className="text-base font-sora">{m.score_total}%</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-acre-dark truncate">{m.titulo}</p>
                  <p className="text-xs text-gray-400">{m.empresa} · {m.cidade_vaga}</p>
                  <div className="flex gap-1 mt-1.5">
                    <span className="badge-gray capitalize text-xs">{m.modalidade}</span>
                    {m.salario_min && <span className="badge-green text-xs">R$ {Number(m.salario_min).toLocaleString('pt-BR')}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
